import {
    CORE_SIZE__SINGLEMODE,
    CORE_SIZE__MULTIMODE_50,
    CORE_SIZE__MULTIMODE_62_5,
    TEST_PLAN_TEMPLATE__DUPLEX,
    ASSEMBLY_LENGTH__LT_100_M,
    RL_MODE__STANDARD
} from '@shared/constants/constants';
import { WizardState } from '@shared/types/test-plan/types';

const PT1_SIZES = [
    12, 24, 36, 48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288
];

export function updateRequiredEquipment(curState: WizardState, prevState: WizardState, setState) {
    if (
        curState.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
        curState.equipment.switch0 !== prevState.equipment.switch0 ||
        curState.equipment.switch1 !== prevState.equipment.switch1 ||
        curState.equipment.switch2 !== prevState.equipment.switch2 ||
        curState.wavelength_850 !== prevState.wavelength_850 ||
        curState.wavelength_1300 !== prevState.wavelength_1300 ||
        curState.wavelength_1310 !== prevState.wavelength_1310 ||
        curState.wavelength_1490 !== prevState.wavelength_1490 ||
        curState.wavelength_1550 !== prevState.wavelength_1550 ||
        curState.wavelength_1625 !== prevState.wavelength_1625 ||
        curState.assembly.coreSize !== prevState.assembly.coreSize ||
        curState.equipment.detectors !== prevState.equipment.detectors ||
        curState.testPlanTemplate !== prevState.testPlanTemplate ||
        curState.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
        curState.equipment.useSwitch2 !== prevState.equipment.useSwitch2 ||
        curState.assembly.testPolarity !== prevState.assembly.testPolarity ||
        curState.fibersPerCable !== prevState.fibersPerCable ||
        curState.cablesPerAssembly !== prevState.cablesPerAssembly ||
        curState.assemblyLength !== prevState.assemblyLength ||
        curState.equipment.rlMode !== prevState.equipment.rlMode
    ) {
        setState((state: WizardState) => {
            let rl1: string | null = null;
            let rds: string | null = null;
            let sd: string | null = null;
            let switch1: string | null = null;
            let switch2: string | null = null;
            let pt1: string | null = null;
            // RLM-110 is required (rather than RLM-100) for multimode assemblies that
            // are either short (< 100m) or run in Standard RL mode. Derived purely from
            // state here so callers never have to set the flag themselves.
            let rl110Required = false;

            if (
                state.assembly.insertionLossSideA ||
                state.assembly.insertionLossSideB ||
                state.assembly.returnLossSideA ||
                state.assembly.returnLossSideB ||
                state.assembly.returnLossTotal ||
                state.assembly.insertionLossTotal
            ) {
                //
                // RL1
                if(state.assembly.coreSize !== CORE_SIZE__SINGLEMODE && (
                    state.assemblyLength === ASSEMBLY_LENGTH__LT_100_M ||
                    state.equipment.rlMode === RL_MODE__STANDARD
                )) {
                    rl110Required = true;
                }

                if(rl110Required) {
                    rl1 = 'RLM-110-';
                } else {
                    rl1 = 'RLM-100-';
                }

                let switch0 = new Set(state.equipment.switch0);
                rl1 += switch0.has(1) && switch0.has(2) ? '02-' : '01-';

                if (state.assembly.coreSize === CORE_SIZE__SINGLEMODE) {
                    let temp = '';
                    temp += state.wavelength_1310 ? '3' : '';
                    temp += state.wavelength_1490 ? '4' : '';
                    temp += state.wavelength_1550 ? '5' : '';
                    temp += state.wavelength_1625 ? '6' : '';

                    temp = temp.padEnd(4, '0');
                    rl1 += temp;

                    rl1 += '-09-xxxx';
                } else {
                    let temp = '';
                    temp += state.wavelength_850 ? '8' : '';
                    temp += state.wavelength_1300 ? '3' : '';

                    temp = temp.padEnd(4, '0');
                    rl1 += temp;
                    rl1 += `-S-${state.assembly.coreSize === CORE_SIZE__MULTIMODE_50 ? '50' : '62'}-xx-xx`;
                }

                //
                // RD-S
                //

                let powerMeters = new Set(state.equipment.detectors);
                if (powerMeters.size !== 1) {
                    rds = powerMeters.size + ' RD-S';
                }

                //
                // SD
                //

                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__DUPLEX) {
                    sd = 'SD34';
                }

                //
                // SX1 - Switch1
                //

                if (state.equipment.useSwitch1) {
                    switch1 = 'OSX-100-1A-';

                    let switchSize = '' + Math.max(...state.equipment.switch1.map(sw => Number(sw)));
                    switch1 += switchSize.padStart(3, '0') + '-';

                    if (state.assembly.coreSize === CORE_SIZE__SINGLEMODE) {
                        switch1 += '09';
                    } else if (state.assembly.coreSize === CORE_SIZE__MULTIMODE_50) {
                        switch1 += '50';
                    } else if (state.assembly.coreSize === CORE_SIZE__MULTIMODE_62_5) {
                        switch1 += '62';
                    }

                    switch1 += '-XX-00B-xx';
                }

                //
                // SX1 - Switch2
                //

                if (state.equipment.useSwitch2) {
                    switch2 = 'OSX-100-1A-';

                    let switchSize = '' + Math.max(...state.equipment.switch2.map(sw => Number(sw)));
                    switch2 += switchSize.padStart(3, '0') + '-';

                    if (state.assembly.coreSize === CORE_SIZE__SINGLEMODE) {
                        switch2 += '09';
                    } else if (state.assembly.coreSize === CORE_SIZE__MULTIMODE_50) {
                        switch2 += '50';
                    } else if (state.assembly.coreSize === CORE_SIZE__MULTIMODE_62_5) {
                        switch2 += '62';
                    }

                    switch2 += '-XX-00B-xx';
                }
            }
            if(state.assembly.testPolarity) {
                pt1 = 'PTM-100-';

                let fibers = state.fibersPerCable * state.cablesPerAssembly;

                let pt1Size = 0;
                for (let i = 0; i < PT1_SIZES.length; i++) {
                    if (fibers <= PT1_SIZES[i]) {
                        pt1Size = PT1_SIZES[i];
                        break;
                    }
                }
                pt1 += pt1Size + '-MA-x';
            }

            return {
                requiredEquipment: {
                    ...state.requiredEquipment,
                    rl1: rl1,
                    rds: rds,
                    sd: sd,
                    switch1: switch1,
                    switch2: switch2,
                    pt1: pt1
                }
            };
        });
    }
}
